import { simplifyPolygon } from '../../../../../shared/page-region/region-geometry';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  OnDestroy,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  formatDistance,
  formatDistancePoints,
  formatRectangle,
  formatRectanglePoints,
  FormattedDistance,
  FormattedRectangle,
  FormattedSurfaceArea,
  formatSurfaceArea,
  formatSurfaceAreaPoints,
  DEFAULT_IMAGE_SEARCH_DPI,
} from './measure-utils';
import { NormalizedLine, PageSizePoints } from './scale-calibration';
import { clamp01, displayToPage, pageToDisplay, PageRotation } from '../../../../../shared/page-region/region-geometry';
import {
  AreaGeometry,
  AreaShape,
  NormalizedPoint,
  areaInPagePoints,
  areaOutline,
  perimeterInPagePoints,
  areaShapeOption,
  polygonAreaInPagePoints,
  validatePolygon,
} from './area-geometry';
import { AreaUnionPolygon, AreaUnionSummary, summarizeAreaUnion } from './area-union';

interface AreaObject {
  id: number;
  geometry: AreaGeometry;
}

interface AreaSessionSnapshot {
  objects: AreaObject[];
  selectedId: number | null;
}

interface AreaObjectView {
  id: number;
  kind: AreaGeometry['kind'];
  label: string;
  selected: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  cx?: number;
  cy?: number;
  rx?: number;
  ry?: number;
  points?: string;
}

/** Minimum screen-pixel gap between recorded freehand samples, filtering pointer jitter. */
const FREEHAND_MIN_DISTANCE_PX = 2;

/** Fraction of the displayed page occupied by each side of a keyboard-created rectangle. */
const KEYBOARD_RECTANGLE_SIZE = 0.3;

interface AreaObjectDrag {
  pointerId: number;
  objectId: number;
  pointerStart: NormalizedPoint;
  originalGeometry: AreaGeometry;
  mutationRecorded: boolean;
}

/**
 * Active measurement tool mode. `calibrate` traces the reference line for a manual page-scale
 * calibration (Issue #2442); it draws the same line as `ruler` and lets its ends be adjusted.
 */
export type MeasureMode = 'ruler' | 'area' | 'calibrate' | null;

/** Which end of the calibration line a handle moves. */
export type LineEnd = 'start' | 'end';

/** Input types that take no typed text, so keys pressed on them are not text editing. */
const NON_TEXT_INPUT_TYPES = new Set(['radio', 'checkbox', 'button', 'submit', 'reset', 'range', 'color', 'file']);

/** True when a point is inside a box grown by `margin` pixels on every side. */
export function pointerNearBox(
  x: number,
  y: number,
  box: { left: number; top: number; right: number; bottom: number },
  margin: number
): boolean {
  return x >= box.left - margin && x <= box.right + margin && y >= box.top - margin && y <= box.bottom + margin;
}

/** A box in the overlay's own pixel coordinates. */
export interface CardBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Window width at or below which the area result is a bottom sheet; matches the stylesheet. */
export const AREA_SHEET_MAX_WIDTH = 600;

/** Gap between the area sheet and the edges of the Preview, in pixels; matches the stylesheet. */
const AREA_SHEET_MARGIN = 8;

/**
 * How far above the bottom of the Preview's container the area sheet sits, so that it ends just
 * above the bottom of the window even when the container runs past it.
 */
export function sheetBottomOffset(containerBottom: number, windowHeight: number): number {
  return Math.max(0, containerBottom - windowHeight) + AREA_SHEET_MARGIN;
}

/**
 * Shortest expanded sheet worth capping to, in pixels; below this the stylesheet's limit applies.
 * The folded sheet is about 130 to 170 px tall. A cap near that height left no room for the details,
 * so on a short phone the handle that opens them appeared to do nothing.
 */
const AREA_SHEET_MIN_HEIGHT = 200;

/**
 * The height that keeps the area sheet below a page that ends above the bottom of the window, or
 * null when the page reaches so far down that a usable sheet has to overlap it. An open sheet that
 * overlaps the page folds again as soon as the page is touched.
 */
export function sheetMaxHeightBelowPage(pageBottom: number, windowHeight: number): number | null {
  const room = windowHeight - AREA_SHEET_MARGIN - (pageBottom + AREA_SHEET_MARGIN);
  return room >= AREA_SHEET_MIN_HEIGHT ? Math.floor(room) : null;
}

/** Gap kept between the Preview toolbar and a result card placed under it, in pixels. */
const TOOLBAR_GAP = 8;

/**
 * Where a result card's top edge goes so that it never covers the Preview toolbar (Issue #2542).
 *
 * The zoom percentage, the active tool and its exit button must stay visible and clickable while a
 * measurement is shown. A card that would overlap the toolbar starts just below it instead; a card
 * that does not reach the toolbar stays where it was placed.
 */
export function topClearOfToolbar(card: CardBox, toolbar: CardBox | null): number {
  if (!toolbar) return card.top;
  const sharesColumns = card.left < toolbar.left + toolbar.width && toolbar.left < card.left + card.width;
  const sharesRows = card.top < toolbar.top + toolbar.height + TOOLBAR_GAP && toolbar.top < card.top + card.height;
  return sharesColumns && sharesRows ? toolbar.top + toolbar.height + TOOLBAR_GAP : card.top;
}

/**
 * Transparent measurement overlay for the PDF preview.
 *
 * Renders on top of the canvas to capture mouse events and draw
 * measurement guides (ruler polyline or area rectangle) via SVG.
 * Displays a floating info card with distance/area in multiple units.
 *
 * Since Issue #2542 the toolbar offers two tools, Ruler and Select area. Using the ruler's line as
 * the reference of a scale calibration is not a separate tool: the Preview switches this overlay to
 * `calibrate` while the calibration form is open and back to `ruler` when it closes, and the line
 * is kept across both switches.
 *
 * In `calibrate` mode (Issue #2442) the same line is used to trace a dimension of known length.
 * Once both ends are placed the line stays on screen with a handle at each end, which can be
 * dragged with a mouse or finger or moved with the arrow keys. The component only reports where
 * the line is; the Preview owns the calibration form and the save.
 *
 * Related: Issue #1212
 */
@Component({
  selector: 'app-measure-overlay',
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './measure-overlay.component.html',
  styleUrls: ['./measure-overlay.component.scss', './measure-overlay-calibration.scss'],
})
export class MeasureOverlayComponent implements OnChanges, OnDestroy {
  private static readonly SNAP_ANGLE_DEG = 15;
  private static readonly MIN_SEGMENT_PX = 0.5;

  // ── Inputs ────────────────────────────────────────────────────
  @Input() mode: MeasureMode = null;
  @Input() areaShape: AreaShape = 'rectangle';
  @Input() canvasWidth = 0;
  @Input() canvasHeight = 0;
  @Input() canvasScale = 1;
  @Input() canvasOffsetX = 0;
  @Input() canvasOffsetY = 0;
  @Input() currentScale = 1.0;
  @Input() drawingScaleText: string | null = null;
  @Input() drawingScaleFactor: number | null = null;
  @Input() scaleDetecting = false;
  /** Correction factor for scanned PDFs with bogus MediaBox (Issue #1151). */
  @Input() pageSizeCorrectionFactor = 1.0;
  /**
   * Size of the page as displayed, in PDF points (Issue #2442). When known, lengths are computed
   * from the measurement's position on the page times this size, so a reading does not depend on
   * zoom, device pixel ratio or the size of the rendered canvas.
   */
  @Input() pageSizePoints: PageSizePoints | null = null;
  /**
   * How the page is turned (Issue #2542). Shapes and lines are kept as fractions of the page as
   * displayed, so when the page turns they are turned with it and stay on the same drawing.
   */
  @Input() pageRotation: PageRotation = 0;
  /** Which scale the readings use, e.g. "Manually calibrated · 1:48" (Issue #2442). */
  @Input() scaleSourceLabel: string | null = null;
  /** True when the readings use a manual calibration (Issue #2442). */
  @Input() manuallyCalibrated = false;
  /**
   * A line to show, as fractions of the displayed page (Issue #2442). In `calibrate` mode it places
   * a line without pointing at the page, or shows the saved reference when recalibrating. In
   * `ruler` mode it shows the reference line as an ordinary measurement straight after a
   * calibration is applied, so the reviewer sees it read the length they entered.
   */
  @Input() presetLine: NormalizedLine | null = null;

  // ── Outputs ───────────────────────────────────────────────────
  @Output() measurementClear = new EventEmitter<void>();
  /**
   * The calibration line after the reviewer places or moves it, as fractions of the displayed page;
   * null while there is no complete line (Issue #2442).
   */
  @Output() calibrationLineChange = new EventEmitter<NormalizedLine | null>();
  /** "Use as scale reference" on a finished single-segment ruler measurement (Issue #2542). */
  @Output() useAsScaleReference = new EventEmitter<NormalizedLine>();
  /**
   * "Use in chat" on one measured area (Issue #2542): its outline as fractions of the displayed
   * page, for the chat attachment workflow of Issue #1747.
   */
  @Output() areaUseInChat = new EventEmitter<NormalizedPoint[]>();
  /**
   * Escape or the Clear all button removed all finished areas (Issue #2542): how many there were,
   * so the Preview can say so and offer Undo.
   */
  @Output() areasCleared = new EventEmitter<number>();

  /**
   * True while the chat attachment review of one area is open (Issue #2542). The areas cannot be
   * edited meanwhile, so the image under review is the image that is sent.
   */
  @Input() chatReview = false;

  // ── State ─────────────────────────────────────────────────────
  /** Area keeps its two-click flow; ruler stays anchored while vertices are added. */
  phase: 'idle' | 'anchored' | 'complete' = 'idle';

  /**
   * Normalized anchor/cursor coordinates (0–1 relative to canvas dimensions).
   * Stored as fractions so that zoom changes (which resize the canvas) don’t
   * invalidate the measurement.
   */
  private anchorNormX = 0;
  private anchorNormY = 0;
  private cursorNormX = 0;
  private cursorNormY = 0;

  /** Ruler vertices are normalized so zoom and canvas resizing preserve the path. */
  private rulerVerticesNorm: NormalizedPoint[] = [];

  /** Polygon vertices use the same normalized page coordinate system. */
  private freehandPointerId: number | null = null;
  private polygonVerticesNorm: NormalizedPoint[] = [];

  /** Unit system toggle. */
  useMetric = false;

  /** Formatted measurement data for the info card. */
  rulerData: FormattedDistance | null = null;
  rulerLiveData: FormattedDistance | null = null;
  areaData: FormattedRectangle | null = null;
  surfaceData: FormattedSurfaceArea | null = null;
  polygonError: string | null = null;

  /** Committed measurements remain visible until Area mode is closed or Clear is used. */
  areaObjects: AreaObject[] = [];
  selectedAreaObjectId: number | null = null;
  /**
   * Whether ground covered by several areas counts once (the default, requester's feedback on
   * #2542) or once per area. Counting once is what a reviewer adding up floor area expects.
   */
  excludeAreaOverlaps = true;
  areaSessionSummary: AreaUnionSummary | null = null;
  areaPreviewSummary: AreaUnionSummary | null = null;
  cumulativeSurfaceData: FormattedSurfaceArea | null = null;
  previewCumulativeSurfaceData: FormattedSurfaceArea | null = null;
  grossSurfaceData: FormattedSurfaceArea | null = null;
  overlapSurfaceData: FormattedSurfaceArea | null = null;
  coveredSurfaceData: FormattedSurfaceArea | null = null;
  selectedSurfaceData: FormattedSurfaceArea | null = null;
  /** Outline lengths (Issue #2542): of all areas, of the selected one, and of the live preview. */
  cumulativePerimeterData: FormattedDistance | null = null;
  previewPerimeterData: FormattedDistance | null = null;
  selectedPerimeterData: FormattedDistance | null = null;

  private nextAreaObjectId = 1;
  private areaUndoStack: AreaSessionSnapshot[] = [];
  private areaRedoStack: AreaSessionSnapshot[] = [];
  private summarizedAreaObjects: readonly AreaObject[] | null = null;
  private summarizedAreaSize: PageSizePoints | null = null;
  private areaObjectDrag: AreaObjectDrag | null = null;
  private readonly onDocumentKeydownListener = (event: KeyboardEvent): void => this.onDocumentKeyDown(event);

  /** Reference to the overlay element for coordinate calculations. */
  @ViewChild('overlayEl') overlayEl!: ElementRef<HTMLDivElement>;
  @ViewChild('resultCard', { read: ElementRef }) resultCard?: ElementRef<HTMLElement>;

  /** True for a moment after a calibration recalculated this measurement (Issue #2542). */
  justRecalculated = false;
  private recalculatedTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Moves focus to the result card and briefly highlights it, so a reviewer who just applied a
   * calibration sees the reference line now read the length they entered (UX note 7, Issue #2542).
   */
  focusResult(): void {
    this.justRecalculated = true;
    if (this.recalculatedTimer) clearTimeout(this.recalculatedTimer);
    this.recalculatedTimer = setTimeout(() => {
      this.justRecalculated = false;
      this.recalculatedTimer = null;
    }, 2400);
    setTimeout(() => this.resultCard?.nativeElement.focus({ preventScroll: true }));
  }

  constructor() {
    if (typeof window !== 'undefined') {
      // Enter and Escape must remain cancelable while a tool owns them. Angular's shared document
      // listener can be passive in the deployed runtime, so use the same explicit capture listener
      // as Preview and text selection.
      window.addEventListener('keydown', this.onDocumentKeydownListener, { capture: true, passive: false });
      // Browser preference storage is unavailable during server-side rendering.
      if (window.localStorage.getItem('measure-unit-system') === 'metric') {
        this.useMetric = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const rotationChange = changes['pageRotation'];
    if (rotationChange && !rotationChange.firstChange) {
      this.turnWithPage(rotationChange.previousValue ?? 0, this.pageRotation);
    }
    const modeChange = changes['mode'];
    if (modeChange && !modeChange.firstChange && this.keepsLineAcross(modeChange.previousValue, this.mode)) {
      // The ruler line became a calibration reference, or the calibration closed and its reference
      // is an ordinary measurement again (Issue #2542). The line stays; only its role changed.
      this.rulerVerticesNorm =
        this.mode === 'ruler'
          ? [
              { x: this.anchorNormX, y: this.anchorNormY },
              { x: this.cursorNormX, y: this.cursorNormY },
            ]
          : [];
    } else if (modeChange && !modeChange.firstChange) {
      this.reset();
    } else if (changes['areaShape'] && !changes['areaShape'].firstChange && this.mode === 'area') {
      this.resetAreaDraft();
    }
    if (this.isLineMode && (changes['presetLine'] || changes['mode'])) {
      this.showPresetLine(this.presetLine);
    }
    // Recompute if canvas size or scale changed
    if (this.phase !== 'idle' || this.areaObjects.length > 0) {
      this.recalculate();
    }
  }

  /**
   * Moves every stored point to where the same spot of the page is shown after a rotation (QA on
   * Issue #2542): the finished areas and their undo history, the area or polygon being drawn, and
   * the ruler line. A shape being dragged is let go, because its starting point no longer applies.
   */
  private turnWithPage(previous: PageRotation, next: PageRotation): void {
    if (previous === next) return;
    const turn = (point: NormalizedPoint): NormalizedPoint => pageToDisplay(displayToPage(point, previous), next);
    const turnGeometry = (geometry: AreaGeometry): AreaGeometry =>
      geometry.kind === 'polygon'
        ? { kind: 'polygon', vertices: geometry.vertices.map(turn) }
        : { kind: geometry.kind, start: turn(geometry.start), end: turn(geometry.end) };
    const turnSnapshot = (snapshot: AreaSessionSnapshot): AreaSessionSnapshot => ({
      ...snapshot,
      objects: snapshot.objects.map(object => ({ ...object, geometry: turnGeometry(object.geometry) })),
    });

    this.areaObjects = this.areaObjects.map(object => ({ ...object, geometry: turnGeometry(object.geometry) }));
    this.areaUndoStack = this.areaUndoStack.map(turnSnapshot);
    this.areaRedoStack = this.areaRedoStack.map(turnSnapshot);
    this.areaObjectDrag = null;
    this.draggingEnd = null;
    this.rulerVerticesNorm = this.rulerVerticesNorm.map(turn);
    this.polygonVerticesNorm = this.polygonVerticesNorm.map(turn);
    const anchor = turn({ x: this.anchorNormX, y: this.anchorNormY });
    const cursor = turn({ x: this.cursorNormX, y: this.cursorNormY });
    this.anchorNormX = anchor.x;
    this.anchorNormY = anchor.y;
    this.cursorNormX = cursor.x;
    this.cursorNormY = cursor.y;
  }

  /** True when switching between ruler and calibration with a finished two-end line on screen. */
  private keepsLineAcross(previous: MeasureMode, next: MeasureMode): boolean {
    if (this.phase !== 'complete') return false;
    if (previous === 'calibrate' && next === 'ruler') return true;
    return previous === 'ruler' && next === 'calibrate' && this.rulerVerticesNorm.length === 2;
  }

  /**
   * Apply live CSS transform during gestures or smooth camera transitions.
   * Matches SpotlightOverlayComponent and TextHighlightOverlayComponent (Issue #1748).
   */
  setGestureTransform(transform: string, transformOrigin: string, transition: string): void {
    const el = this.overlayEl?.nativeElement;
    if (!el) return;
    el.style.transformOrigin = transformOrigin;
    el.style.transition = transition;
    el.style.transform = transform;
  }

  // ── Pointer and keyboard handling ─────────────────────────────

  onOverlayPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    // Working on the page again: the phone sheet folds back so it is out of the way, and a new
    // shape starts with the panel in its home corner.
    if (this.areaSheet) this.sheetExpanded = false;
    if (this.phase !== 'anchored') this.dodgedTo = 'left';

    this.overlayEl?.nativeElement.focus({ preventScroll: true });

    const pos = this.getOverlayPosition(event);
    const normX = this.canvasWidth > 0 ? pos.x / this.canvasWidth : 0;
    const normY = this.canvasHeight > 0 ? pos.y / this.canvasHeight : 0;

    if (this.mode === 'ruler') {
      this.handleRulerPointerDown(normX, normY, event.shiftKey);
      return;
    }
    if (this.mode === 'area' && this.areaShape === 'freehand') {
      if (this.freehandPointerId !== null) {
        this.resetAreaDraft();
        return;
      }
      this.resetAreaDraft();
      this.freehandPointerId = event.pointerId;
      this.polygonVerticesNorm = [{ x: normX, y: normY }];
      this.cursorNormX = normX;
      this.cursorNormY = normY;
      this.phase = 'anchored';
      (event.currentTarget as Element | null)?.setPointerCapture?.(event.pointerId);
      this.recalculate();
      return;
    }
    if (this.mode === 'area' && this.areaShape === 'select') {
      this.resetAreaDraft();
      this.selectedAreaObjectId = null;
      this.recalculateAreaSession();
      return;
    }
    if (this.mode === 'area' && this.areaShape === 'polygon') {
      this.handlePolygonPointerDown(normX, normY);
      return;
    }

    // A finished calibration line is adjusted with its handles; a stray click must not discard it.
    if (this.mode === 'calibrate' && this.phase === 'complete') return;

    switch (this.phase) {
      case 'idle':
      case 'complete':
        this.anchorNormX = normX;
        this.anchorNormY = normY;
        this.cursorNormX = normX;
        this.cursorNormY = normY;
        this.phase = 'anchored';
        this.recalculate();
        break;
      case 'anchored': {
        const snapped = this.applySnapping(normX, normY, event.shiftKey);
        this.cursorNormX = snapped.x;
        this.cursorNormY = snapped.y;
        this.phase = 'complete';
        this.recalculate();
        if (this.mode === 'area') this.commitCurrentArea();
        this.emitCalibrationLine();
        break;
      }
    }
  }

  onOverlayPointerMove(event: PointerEvent): void {
    if (this.areaObjectDrag) {
      this.moveAreaObject(event);
      return;
    }
    if (this.phase !== 'anchored') return;
    const drawingFreehand = this.mode === 'area' && this.areaShape === 'freehand';
    if (drawingFreehand && event.pointerId !== this.freehandPointerId) return;

    const pos = this.getOverlayPosition(event);
    const rawNormX = this.canvasWidth > 0 ? pos.x / this.canvasWidth : 0;
    const rawNormY = this.canvasHeight > 0 ? pos.y / this.canvasHeight : 0;

    const snapped = drawingFreehand
      ? { x: Math.max(0, Math.min(1, rawNormX)), y: Math.max(0, Math.min(1, rawNormY)) }
      : this.applySnapping(rawNormX, rawNormY, event.shiftKey);
    this.cursorNormX = snapped.x;
    this.cursorNormY = snapped.y;
    if (drawingFreehand) {
      const last = this.polygonVerticesNorm[this.polygonVerticesNorm.length - 1];
      if (
        !last ||
        Math.hypot((snapped.x - last.x) * this.canvasWidth, (snapped.y - last.y) * this.canvasHeight) >=
          FREEHAND_MIN_DISTANCE_PX
      ) {
        this.polygonVerticesNorm.push({ x: snapped.x, y: snapped.y });
      }
    }
    this.dodgePointer(event.clientX, event.clientY);
    this.recalculate();
  }

  /** Select a committed shape and prepare it for pointer-drag translation. */
  onAreaObjectPointerDown(event: PointerEvent, id: number): void {
    if (this.mode !== 'area' || this.areaShape !== 'select') return;
    const object = this.areaObjects.find(candidate => candidate.id === id);
    if (!object) return;

    event.preventDefault();
    event.stopPropagation();
    this.resetAreaDraft();
    this.selectedAreaObjectId = id;
    const position = this.getOverlayPosition(event);
    this.areaObjectDrag = {
      pointerId: event.pointerId,
      objectId: id,
      pointerStart: {
        x: this.canvasWidth > 0 ? clamp01(position.x / this.canvasWidth) : 0,
        y: this.canvasHeight > 0 ? clamp01(position.y / this.canvasHeight) : 0,
      },
      originalGeometry: this.cloneAreaGeometry(object.geometry),
      mutationRecorded: false,
    };
    (event.currentTarget as Element | null)?.setPointerCapture?.(event.pointerId);
    this.recalculateAreaSession();
    this.overlayEl?.nativeElement.focus({ preventScroll: true });
  }

  /** Translate the active committed shape while preserving its size and keeping it on the page. */
  private moveAreaObject(event: PointerEvent): void {
    const drag = this.areaObjectDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const position = this.getOverlayPosition(event);
    const pointer = {
      x: this.canvasWidth > 0 ? clamp01(position.x / this.canvasWidth) : 0,
      y: this.canvasHeight > 0 ? clamp01(position.y / this.canvasHeight) : 0,
    };
    const moved = this.translateAreaGeometryWithinPage(
      drag.originalGeometry,
      pointer.x - drag.pointerStart.x,
      pointer.y - drag.pointerStart.y
    );
    if (!drag.mutationRecorded && this.sameAreaGeometry(moved, drag.originalGeometry)) return;
    if (!drag.mutationRecorded) {
      this.recordAreaMutation();
      drag.mutationRecorded = true;
    }
    this.areaObjects = this.areaObjects.map(object =>
      object.id === drag.objectId ? { ...object, geometry: moved } : object
    );
    this.recalculateAreaSession();
  }

  onOverlayPointerUp(event: PointerEvent): void {
    if (event.pointerId === this.freehandPointerId) {
      if (event.type === 'pointercancel') {
        this.resetAreaDraft();
        return;
      }
      this.onOverlayPointerMove(event);
      this.freehandPointerId = null;
      (event.currentTarget as Element | null)?.releasePointerCapture?.(event.pointerId);
      this.polygonVerticesNorm = simplifyPolygon(this.polygonVerticesNorm);
      const validation = validatePolygon(this.polygonVerticesNorm);
      this.polygonError = validation.message;
      this.phase = 'complete';
      if (validation.valid) this.commitCurrentArea();
      else this.polygonVerticesNorm = [];
      this.recalculate();
      return;
    }
    const drag = this.areaObjectDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.areaObjectDrag = null;
    (event.target as Element | null)?.releasePointerCapture?.(event.pointerId);
  }

  onOverlayDoubleClick(event: MouseEvent): void {
    const isRuler = this.mode === 'ruler';
    const isPolygon = this.mode === 'area' && this.areaShape === 'polygon';
    if ((!isRuler && !isPolygon) || this.phase !== 'anchored') return;
    event.preventDefault();
    event.stopPropagation();

    const pos = this.getOverlayPosition(event);
    const normX = this.canvasWidth > 0 ? pos.x / this.canvasWidth : 0;
    const normY = this.canvasHeight > 0 ? pos.y / this.canvasHeight : 0;
    const snapped = this.applySnapping(normX, normY, event.shiftKey);
    this.cursorNormX = snapped.x;
    this.cursorNormY = snapped.y;
    if (isRuler) {
      this.finishRuler();
    } else {
      this.finishPolygon();
    }
  }

  onDocumentKeyDown(event: KeyboardEvent): void {
    if (this.isTextEditingTarget(event.target)) return;

    if (event.key === 'Escape') {
      // Escape inside an open menu or dialog belongs to that overlay, and while the chat review of
      // an area is open it belongs to that panel (Issue #2542).
      //
      // "Already handled" is deliberately not the test. A menu that has just been closed keeps
      // receiving page-level key presses while it fades out and marks Escape as handled, so a quick
      // second Escape, meant for the tool, would be lost. Found on the deployed build.
      if (this.isInOverlay(event.target) || this.menuIsOpen() || this.chatReview) return;
      event.preventDefault();
      this.onEscapeKey();
      return;
    }

    const overlayOwnsFocus = event.target === this.overlayEl?.nativeElement;
    if (
      this.mode === 'area' &&
      overlayOwnsFocus &&
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === 'z'
    ) {
      event.preventDefault();
      if (event.shiftKey) this.redoAreaChange();
      else this.undoAreaChange();
      return;
    }

    if (
      this.mode === 'area' &&
      overlayOwnsFocus &&
      this.phase !== 'anchored' &&
      (event.key === 'Delete' || event.key === 'Backspace')
    ) {
      event.preventDefault();
      this.deleteSelectedArea();
      return;
    }

    const isRuler = this.mode === 'ruler';
    const isPolygon = this.mode === 'area' && this.areaShape === 'polygon';
    if ((!isRuler && !isPolygon) || this.phase !== 'anchored') return;
    if (event.target !== this.overlayEl?.nativeElement) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      if (isRuler) this.finishRuler();
      else this.finishPolygon();
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      if (isRuler) this.removeLastRulerVertex();
      else this.removeLastPolygonVertex();
    }
  }

  /**
   * Escape undoes one level at a time (Issue #2542).
   *
   * - A line or polygon with several segments placed: Escape drops only the segment that is being
   *   stretched and finishes the shape at the last vertex that was placed. Enter or a double-click
   *   finishes it at the cursor instead. (Requester's feedback: Escape used to discard the whole
   *   path.) With too few vertices to make a measurement, Escape cancels the shape.
   * - A rectangle or oval being drawn has one segment, so Escape cancels it.
   * - Calibration: Escape closes it at once without changing anything (Issue #2442); the Preview
   *   returns to the ruler.
   * - Then, for areas: a selected area is deselected; then all finished areas are cleared, which
   *   Undo brings back while the tool is still open; then the tool is left.
   * - Then, for the ruler: a finished measurement is cleared; then the tool is left.
   */
  onEscapeKey(): void {
    if (this.mode === 'calibrate') {
      this.reset();
      this.measurementClear.emit();
      return;
    }
    if (this.mode === 'area') {
      if (this.phase === 'anchored') {
        this.endAreaDraftAtLastVertex();
        return;
      }
      if (this.selectedAreaObjectId !== null) {
        // Step back from "one area selected" to "areas shown" before anything is removed.
        this.resetAreaDraft();
        this.selectedAreaObjectId = null;
        this.recalculateAreaSession();
        return;
      }
      if (this.areaObjects.length > 0) {
        const count = this.areaObjects.length;
        this.clearAreaSession();
        this.areasCleared.emit(count);
        return;
      }
      if (this.phase !== 'idle') {
        // A finished polygon that was not accepted as an area, with its message still showing.
        this.resetAreaDraft();
        return;
      }
      this.measurementClear.emit();
      return;
    }
    if (this.phase === 'anchored') {
      this.endRulerAtLastVertex();
      return;
    }
    if (this.phase !== 'idle') {
      this.reset();
      return;
    }
    this.measurementClear.emit();
  }

  /**
   * Escape on a ruler path in progress: keeps the segments already placed and drops the one being
   * stretched. A path with only its first point has nothing to keep and is cancelled.
   */
  private endRulerAtLastVertex(): void {
    const last = this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1];
    if (this.rulerVerticesNorm.length < 2 || !last) {
      this.reset();
      return;
    }
    this.cursorNormX = last.x;
    this.cursorNormY = last.y;
    this.finishRuler();
  }

  /**
   * Escape on an area being drawn. A polygon with at least three corners is closed at the last
   * corner placed, dropping the edge being stretched; if that shape is not a valid area (its edges
   * cross, say) it is cancelled, so Escape can never be stuck on it. Anything else is cancelled.
   */
  private endAreaDraftAtLastVertex(): void {
    const last = this.polygonVerticesNorm[this.polygonVerticesNorm.length - 1];
    if (this.areaShape === 'polygon' && this.polygonVerticesNorm.length >= 3 && last) {
      this.cursorNormX = last.x;
      this.cursorNormY = last.y;
      this.finishPolygon();
      if (this.phase !== 'anchored') return;
    }
    this.resetAreaDraft();
  }

  /** True when a finished ruler measurement is a single straight segment, which can be a scale reference. */
  get canUseAsScaleReference(): boolean {
    return this.mode === 'ruler' && this.phase === 'complete' && this.rulerVerticesNorm.length === 2;
  }

  /** Hands the finished single-segment measurement to the Preview as a calibration reference. */
  onUseAsScaleReference(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canUseAsScaleReference) return;
    const [start, end] = this.rulerVerticesNorm;
    this.anchorNormX = start.x;
    this.anchorNormY = start.y;
    this.cursorNormX = end.x;
    this.cursorNormY = end.y;
    this.useAsScaleReference.emit({ start: { ...start }, end: { ...end } });
  }

  /**
   * The one area that "Use in chat" would send: the selected area, or the only area when there is
   * just one. Null with several areas and none selected, because a chat attachment is one outline.
   */
  get chatAreaObject(): AreaObject | null {
    if (this.mode !== 'area' || this.phase === 'anchored') return null;
    return this.selectedAreaObject ?? (this.areaObjects.length === 1 ? this.areaObjects[0] : null);
  }

  /** Sends the outline of {@link chatAreaObject} to the Preview for the chat attachment workflow. */
  onUseAreaInChat(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = this.chatAreaObject;
    // The button stays focusable when it cannot act, so its hint is reachable; it then does nothing.
    if (!target) return;
    this.areaUseInChat.emit(areaOutline(target.geometry));
  }

  /**
   * True while a menu or the toolbar's Zoom panel is open, as its trigger reports it; false once it
   * has been dismissed.
   */
  private menuIsOpen(): boolean {
    return (
      typeof document !== 'undefined' &&
      !!document.querySelector(
        '[aria-haspopup="menu"][aria-expanded="true"], [aria-haspopup="dialog"][aria-expanded="true"]'
      )
    );
  }

  private isInOverlay(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.cdk-overlay-container');
  }

  /** Finish at the live cursor when non-zero, otherwise at the last committed vertex. */
  finishRuler(): void {
    if (this.mode !== 'ruler' || this.phase !== 'anchored' || !this.canFinishRuler) return;
    this.commitRulerPoint({ x: this.cursorNormX, y: this.cursorNormY });
    this.phase = 'complete';
    this.rulerLiveData = null;
    this.recalculate();
  }

  /** Close and validate the polygon, committing a non-zero live edge first. */
  finishPolygon(): void {
    if (this.mode !== 'area' || this.areaShape !== 'polygon' || this.phase !== 'anchored') return;
    this.commitPolygonPoint({ x: this.cursorNormX, y: this.cursorNormY });
    const validation = validatePolygon(this.polygonVerticesNorm);
    this.polygonError = validation.message;
    if (!validation.valid) {
      this.recalculate();
      return;
    }
    this.phase = 'complete';
    this.recalculate();
    this.commitCurrentArea();
  }

  /** Done/New measurement action for Polygon. */
  onPolygonAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.phase === 'anchored') {
      this.finishPolygon();
    } else if (this.phase === 'complete') {
      this.startNewAreaMeasurement();
    }
  }

  /** Clear a completed area while leaving Area mode active. */
  startNewAreaMeasurement(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.resetAreaDraft();
    this.overlayEl?.nativeElement.focus({ preventScroll: true });
  }

  /** Select a committed area without beginning another measurement. */
  selectAreaObject(event: Event, id: number): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.areaObjects.some(object => object.id === id)) return;
    this.resetAreaDraft();
    this.selectedAreaObjectId = id;
    this.recalculate();
    this.overlayEl?.nativeElement.focus({ preventScroll: true });
  }

  /** Creates an undoable area without a pointer gesture. */
  addKeyboardRectangle(event?: Event): void {
    event?.stopPropagation();
    if (this.mode !== 'area' || this.chatReview) return;
    this.resetAreaDraft();
    this.recordAreaMutation();
    const offset = (1 - KEYBOARD_RECTANGLE_SIZE) / 2;
    const object: AreaObject = {
      id: this.nextAreaObjectId++,
      geometry: { kind: 'rectangle', start: { x: offset, y: offset }, end: { x: 1 - offset, y: 1 - offset } },
    };
    this.areaObjects = [...this.areaObjects, object];
    this.selectedAreaObjectId = object.id;
    this.recalculateAreaSession();
    this.overlayEl?.nativeElement.focus({ preventScroll: true });
  }

  /** Delete the selected committed area and retain the rest of the session. */
  deleteSelectedArea(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.selectedAreaObjectId === null) return;
    const index = this.areaObjects.findIndex(object => object.id === this.selectedAreaObjectId);
    if (index < 0) return;
    this.recordAreaMutation();
    this.areaObjects = this.areaObjects.filter((_, objectIndex) => objectIndex !== index);
    this.selectedAreaObjectId = this.areaObjects[Math.min(index, this.areaObjects.length - 1)]?.id ?? null;
    this.resetAreaDraft();
  }

  /** Restore the previous add/delete/clear mutation. */
  undoAreaChange(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const previous = this.areaUndoStack.pop();
    if (!previous) return;
    this.areaRedoStack.push(this.areaSnapshot());
    this.restoreAreaSnapshot(previous);
  }

  /** Reapply the most recently undone mutation. */
  redoAreaChange(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const next = this.areaRedoStack.pop();
    if (!next) return;
    this.areaUndoStack.push(this.areaSnapshot());
    this.restoreAreaSnapshot(next);
  }

  /**
   * Remove every committed area as one undoable action. When the reviewer asks for it, from the
   * Clear all button, the Preview is told how many areas went, so it can say so and offer Undo: on
   * a touch screen the button is easy to hit by accident (found by UX on the deployed build).
   */
  clearAreaSession(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.areaObjects.length === 0) return;
    const count = this.areaObjects.length;
    this.recordAreaMutation();
    this.areaObjects = [];
    this.selectedAreaObjectId = null;
    this.resetAreaDraft();
    if (event) this.areasCleared.emit(count);
  }

  setAreaOverlapMode(excludeOverlaps: boolean, event?: Event): void {
    event?.stopPropagation();
    this.excludeAreaOverlaps = excludeOverlaps;
    this.refreshCumulativeSurfaceData();
  }

  get areaObjectCount(): number {
    return this.areaObjects.length;
  }

  get canUndoArea(): boolean {
    return this.areaUndoStack.length > 0;
  }

  get canRedoArea(): boolean {
    return this.areaRedoStack.length > 0;
  }

  get selectedAreaObject(): AreaObject | null {
    return this.areaObjects.find(object => object.id === this.selectedAreaObjectId) ?? null;
  }

  get selectedAreaObjectLabel(): string | null {
    const selected = this.selectedAreaObject;
    return selected ? areaShapeOption(selected.geometry.kind).label : null;
  }

  get cumulativeAreaText(): string | null {
    const data = this.previewCumulativeSurfaceData ?? this.cumulativeSurfaceData;
    return this.surfaceAreaText(data);
  }

  get selectedAreaText(): string | null {
    return this.surfaceAreaText(this.selectedSurfaceData);
  }

  /** Perimeter of all areas, or of the areas with the shape being drawn; null without a scale. */
  get cumulativePerimeterText(): string | null {
    return this.lengthText(this.previewPerimeterData ?? this.cumulativePerimeterData);
  }

  get selectedPerimeterText(): string | null {
    return this.lengthText(this.selectedPerimeterData);
  }

  get grossAreaText(): string | null {
    return this.surfaceAreaText(this.grossSurfaceData);
  }

  get overlapAreaText(): string | null {
    return this.surfaceAreaText(this.overlapSurfaceData);
  }

  get coveredAreaText(): string | null {
    return this.surfaceAreaText(this.coveredSurfaceData);
  }

  get isPreviewingCumulativeArea(): boolean {
    return this.phase === 'anchored' && this.areaPreviewSummary !== null;
  }

  /** Shapes rendered from their normalized source-page coordinates. */
  get areaObjectViews(): readonly AreaObjectView[] {
    return this.areaObjects.map((object, index) => {
      const geometry = object.geometry;
      const base = {
        id: object.id,
        kind: geometry.kind,
        label: `${areaShapeOption(geometry.kind).label} ${index + 1}`,
        selected: object.id === this.selectedAreaObjectId,
      };
      if (geometry.kind === 'ellipse') {
        const start = this.toScreenPoint(geometry.start);
        const end = this.toScreenPoint(geometry.end);
        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        return {
          ...base,
          cx: Math.min(start.x, end.x) + width / 2,
          cy: Math.min(start.y, end.y) + height / 2,
          rx: width / 2,
          ry: height / 2,
        };
      }
      if (geometry.kind === 'polygon') {
        return {
          ...base,
          points: geometry.vertices
            .map(point => this.toScreenPoint(point))
            .map(point => `${point.x},${point.y}`)
            .join(' '),
        };
      }
      const start = this.toScreenPoint(geometry.start);
      const end = this.toScreenPoint(geometry.end);
      return {
        ...base,
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y),
      };
    });
  }

  /** Exterior, disconnected, and hole boundaries after overlap union. */
  get areaUnionPaths(): readonly string[] {
    if (!this.excludeAreaOverlaps) return [];
    return (this.areaSessionSummary?.polygons ?? []).map(polygon => this.unionPolygonPath(polygon));
  }

  /** Keep one focused action control while its behavior changes from Done to New measurement. */
  onRulerAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.phase === 'anchored') {
      this.finishRuler();
    } else if (this.phase === 'complete') {
      this.startNewRulerMeasurement();
    }
  }

  /** Clear the completed result while leaving ruler mode active. */
  startNewRulerMeasurement(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.reset();
    this.overlayEl?.nativeElement.focus({ preventScroll: true });
  }

  // ── Calibration line handles (Issue #2442) ────────────────────

  /** The end of the calibration line being dragged, if any. */
  private draggingEnd: LineEnd | null = null;

  /** Starts dragging one end of the calibration line with a mouse, pen or finger. */
  onHandlePointerDown(event: PointerEvent, end: LineEnd): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingEnd = end;
    (event.target as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
  }

  onHandlePointerMove(event: PointerEvent, end: LineEnd): void {
    if (this.draggingEnd !== end) return;
    event.preventDefault();
    const pos = this.getOverlayPosition(event);
    this.moveLineEnd(
      end,
      this.canvasWidth > 0 ? pos.x / this.canvasWidth : 0,
      this.canvasHeight > 0 ? pos.y / this.canvasHeight : 0
    );
  }

  onHandlePointerUp(event: PointerEvent): void {
    if (!this.draggingEnd) return;
    this.draggingEnd = null;
    (event.target as HTMLElement | null)?.releasePointerCapture?.(event.pointerId);
  }

  /** Moves one end of the calibration line with the arrow keys: one pixel, or ten with Shift. */
  onHandleKeydown(event: KeyboardEvent, end: LineEnd): void {
    const step = event.shiftKey ? 10 : 1;
    let dx = 0;
    let dy = 0;
    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    const x = end === 'start' ? this.anchorNormX : this.cursorNormX;
    const y = end === 'start' ? this.anchorNormY : this.cursorNormY;
    this.moveLineEnd(
      end,
      x + (this.canvasWidth > 0 ? dx / this.canvasWidth : 0),
      y + (this.canvasHeight > 0 ? dy / this.canvasHeight : 0)
    );
  }

  /** Places one end of the calibration line, keeping it on the page, and reports the new line. */
  private moveLineEnd(end: LineEnd, normX: number, normY: number): void {
    if (end === 'start') {
      this.anchorNormX = clamp01(normX);
      this.anchorNormY = clamp01(normY);
    } else {
      this.cursorNormX = clamp01(normX);
      this.cursorNormY = clamp01(normY);
    }
    this.recalculate();
    this.emitCalibrationLine();
  }

  /**
   * Shows a line supplied by the Preview. When the Preview withdraws the line during calibration
   * the traced line is cleared so a new one can be drawn; a ruler measurement is left alone.
   */
  private showPresetLine(line: NormalizedLine | null): void {
    if (!line) {
      if (this.mode === 'calibrate' && this.phase === 'complete') this.reset();
      return;
    }
    this.anchorNormX = line.start.x;
    this.anchorNormY = line.start.y;
    this.cursorNormX = line.end.x;
    this.cursorNormY = line.end.y;
    if (this.mode === 'ruler') {
      this.rulerVerticesNorm = [{ ...line.start }, { ...line.end }];
    }
    this.phase = 'complete';
    this.recalculate();
  }

  private emitCalibrationLine(): void {
    if (this.mode !== 'calibrate') return;
    this.calibrationLineChange.emit(
      this.phase === 'complete'
        ? { start: { x: this.anchorNormX, y: this.anchorNormY }, end: { x: this.cursorNormX, y: this.cursorNormY } }
        : null
    );
  }

  /** Accessible name of the overlay region for the active tool. */
  get overlayLabel(): string {
    if (this.mode === 'calibrate') return 'Scale calibration overlay';
    if (this.mode === 'ruler') return 'Ruler measurement overlay';
    if (this.areaShape === 'select') return 'Area selection and movement overlay';
    return `${this.selectedAreaShapeLabel} area measurement overlay`;
  }

  get overlayDescription(): string {
    if (this.mode === 'ruler') {
      return 'Click or tap to add path vertices. Press Enter or use Done to finish. Backspace removes the last vertex and Escape cancels.';
    }
    if (this.mode === 'calibrate') {
      return 'Click or tap twice to trace a known dimension. Use the endpoint handles to adjust it. Escape cancels.';
    }
    if (this.areaShape === 'select') {
      return 'Select a committed shape and drag it to move it. Delete removes the selected shape.';
    }
    if (this.areaShape === 'freehand')
      return 'Drag around an area and release to close it. Escape cancels. Use Add rectangle to create an area with the keyboard.';
    if (this.areaShape === 'polygon') {
      return 'Click or tap to add polygon vertices. Press Enter or use Done to close it. Backspace removes the last vertex and Escape cancels.';
    }
    return `Click or tap twice to measure a ${this.selectedAreaShapeLabel.toLowerCase()}. Hold Shift for ${
      this.areaShape === 'rectangle' ? 'a square' : 'a circle'
    }. Escape cancels.`;
  }

  get idlePromptIcon(): string {
    if (this.mode === 'calibrate') return 'architecture';
    return this.mode === 'ruler' ? 'straighten' : this.selectedAreaShapeIcon;
  }

  get idlePromptText(): string {
    if (this.mode === 'calibrate') return 'Mark both ends of a known dimension';
    if (this.mode === 'ruler') return 'Click to start measuring';
    if (this.areaShape === 'select') return 'Select a shape to move it';
    if (this.areaShape === 'freehand') return 'Drag around an area';
    return `Click to start a ${this.selectedAreaShapeLabel.toLowerCase()}`;
  }

  /** True for the two modes that draw a line between two points. */
  get isLineMode(): boolean {
    return this.mode === 'ruler' || this.mode === 'calibrate';
  }

  /** Length of the current line in PDF points, or null when the page size is not known. */
  get lineLengthPoints(): number | null {
    const size = this.pageSizePoints;
    if (!size) return null;
    return Math.hypot(
      (this.cursorNormX - this.anchorNormX) * size.width,
      (this.cursorNormY - this.anchorNormY) * size.height
    );
  }

  /** Screen-pixel length of the two-end calibration line when PDF page dimensions are unavailable. */
  private get calibrationLineLength(): number {
    return Math.hypot(this.cursorX - this.anchorX, this.cursorY - this.anchorY);
  }

  /** Total ruler path length in PDF points, independent of zoom and render resolution. */
  get rulerTotalLengthPoints(): number | null {
    const size = this.pageSizePoints;
    if (!size) return null;
    return this.rulerCommittedLengthPoints + this.rulerLiveLengthPoints;
  }

  private get rulerCommittedLengthPoints(): number {
    let total = 0;
    for (let index = 1; index < this.rulerVerticesNorm.length; index++) {
      total += this.segmentLengthPoints(this.rulerVerticesNorm[index - 1], this.rulerVerticesNorm[index]);
    }
    return total;
  }

  private get rulerLiveLengthPoints(): number {
    if (this.phase !== 'anchored' || this.rulerVerticesNorm.length === 0) return 0;
    return this.segmentLengthPoints(this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1], {
      x: this.cursorNormX,
      y: this.cursorNormY,
    });
  }

  // ── Toggle unit system ────────────────────────────────────────

  toggleUnitSystem(event: MouseEvent): void {
    // Prevent click from bubbling to overlay mousedown which resets the measurement
    event.preventDefault();
    event.stopPropagation();
    this.useMetric = !this.useMetric;
    localStorage.setItem('measure-unit-system', this.useMetric ? 'metric' : 'imperial');
  }

  // ── SVG geometry helpers (derived from normalized coords) ──────

  /** Screen-pixel positions derived from normalized coordinates. */
  get anchorX(): number {
    return this.anchorNormX * this.canvasWidth;
  }
  get anchorY(): number {
    return this.anchorNormY * this.canvasHeight;
  }
  get cursorX(): number {
    return this.cursorNormX * this.canvasWidth;
  }
  get cursorY(): number {
    return this.cursorNormY * this.canvasHeight;
  }

  get rulerVertexCount(): number {
    return this.rulerVerticesNorm.length;
  }

  /** Committed vertices in current canvas pixels, used by SVG and focused tests. */
  get rulerVertexPoints(): ReadonlyArray<{ x: number; y: number }> {
    return this.rulerVerticesNorm.map(point => this.toScreenPoint(point));
  }

  get rulerCommittedLength(): number {
    let total = 0;
    for (let index = 1; index < this.rulerVerticesNorm.length; index++) {
      total += this.segmentLength(this.rulerVerticesNorm[index - 1], this.rulerVerticesNorm[index]);
    }
    return total;
  }

  get rulerLiveLength(): number {
    if (this.phase !== 'anchored' || this.rulerVerticesNorm.length === 0) return 0;
    return this.segmentLength(this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1], {
      x: this.cursorNormX,
      y: this.cursorNormY,
    });
  }

  get rulerTotalLength(): number {
    return this.rulerCommittedLength + this.rulerLiveLength;
  }

  get canFinishRuler(): boolean {
    return this.rulerCommittedLength >= MeasureOverlayComponent.MIN_SEGMENT_PX || this.hasNonZeroLiveSegment();
  }

  get rulerPolylinePoints(): string {
    const points = [...this.rulerVertexPoints];
    if (this.hasNonZeroLiveSegment()) {
      points.push({ x: this.cursorX, y: this.cursorY });
    }
    return points.map(point => `${point.x},${point.y}`).join(' ');
  }

  /** Rectangle computed from anchor → cursor (for area tool). */
  get rectLeft(): number {
    return Math.min(this.anchorX, this.cursorX);
  }
  get rectTop(): number {
    return Math.min(this.anchorY, this.cursorY);
  }
  get rectWidth(): number {
    return Math.abs(this.cursorX - this.anchorX);
  }
  get rectHeight(): number {
    return Math.abs(this.cursorY - this.anchorY);
  }

  get ellipseCenterX(): number {
    return this.rectLeft + this.rectWidth / 2;
  }

  get ellipseCenterY(): number {
    return this.rectTop + this.rectHeight / 2;
  }

  get ellipseRadiusX(): number {
    return this.rectWidth / 2;
  }

  get ellipseRadiusY(): number {
    return this.rectHeight / 2;
  }

  get polygonVertexCount(): number {
    return this.polygonVerticesNorm.length;
  }

  get polygonVertexPoints(): ReadonlyArray<{ x: number; y: number }> {
    return this.polygonVerticesNorm.map(point => this.toScreenPoint(point));
  }

  get polygonPolylinePoints(): string {
    const points = [...this.polygonVertexPoints];
    if (this.phase === 'anchored' && this.hasNonZeroPolygonLiveSegment()) {
      points.push({ x: this.cursorX, y: this.cursorY });
    }
    return points.map(point => `${point.x},${point.y}`).join(' ');
  }

  get polygonFirstPoint(): { x: number; y: number } | null {
    return this.polygonVertexPoints[0] ?? null;
  }

  get polygonClosingStart(): { x: number; y: number } | null {
    if (this.polygonVerticesNorm.length < 2) return null;
    if (this.phase === 'anchored' && this.hasNonZeroPolygonLiveSegment()) {
      return { x: this.cursorX, y: this.cursorY };
    }
    return this.polygonVertexPoints[this.polygonVertexPoints.length - 1] ?? null;
  }

  get hasNonZeroPolygonLiveSegmentForTemplate(): boolean {
    return this.hasNonZeroPolygonLiveSegment();
  }

  get canFinishPolygon(): boolean {
    return (
      this.polygonVerticesNorm.length >= 3 ||
      (this.polygonVerticesNorm.length >= 2 && this.hasNonZeroPolygonLiveSegment())
    );
  }

  get selectedAreaShapeLabel(): string {
    return areaShapeOption(this.areaShape).label;
  }

  get selectedAreaShapeIcon(): string {
    return areaShapeOption(this.areaShape).icon;
  }

  /** Current normalized shape. Polygon area is exposed only after a valid finish. */
  get areaGeometry(): AreaGeometry | null {
    if (this.mode !== 'area' || this.phase === 'idle' || this.areaShape === 'select') return null;
    if (this.areaShape === 'polygon' || this.areaShape === 'freehand') {
      if (this.phase !== 'complete' || !validatePolygon(this.polygonVerticesNorm).valid) return null;
      return { kind: 'polygon', vertices: this.polygonVerticesNorm.map(point => ({ ...point })) };
    }
    return {
      kind: this.areaShape,
      start: { x: this.anchorNormX, y: this.anchorNormY },
      end: { x: this.cursorNormX, y: this.cursorNormY },
    };
  }

  /** Card position — dynamically placed to avoid overlapping the measurement. */
  get cardLeft(): number {
    const cardWidth = Math.min(320, Math.max(190, this.canvasWidth - 16));
    const maxLeft = Math.max(8, this.canvasWidth - cardWidth - 8);
    let raw: number;
    if (this.mode === 'area') {
      raw =
        this.cursorX >= this.anchorX
          ? Math.max(this.cursorX, this.anchorX) + 16
          : Math.min(this.cursorX, this.anchorX) - cardWidth - 16;
    } else {
      raw = this.cursorX + 16 + cardWidth <= this.canvasWidth ? this.cursorX + 16 : this.cursorX - cardWidth - 16;
    }
    return Math.max(8, Math.min(raw, maxLeft));
  }

  get cardTop(): number {
    // The rectangle result has the tallest card. CSS adds scrolling when the page itself is shorter.
    const cardHeight = this.mode === 'area' ? 420 : 330;
    const maxTop = Math.max(8, this.canvasHeight - Math.min(cardHeight, this.canvasHeight - 16) - 8);
    const below = this.cursorY + 16;
    const raw = below + cardHeight <= this.canvasHeight ? below : this.cursorY - cardHeight - 16;
    const top = Math.max(8, Math.min(raw, maxTop));
    const cardWidth = Math.min(320, Math.max(190, this.canvasWidth - 16));
    const measuredHeight = this.resultCard?.nativeElement.offsetHeight || cardHeight;
    return topClearOfToolbar(
      { left: this.cardLeft, top, width: cardWidth, height: measuredHeight },
      this.toolbarInOverlay()
    );
  }

  /** The measurement result, as a template that is drawn here or by the Preview; see the template. */
  @ViewChild('resultTemplate') resultTemplate?: TemplateRef<unknown>;

  /**
   * True when the Preview draws this overlay's phone sheet in its own container. Without a host
   * that does so, the sheet is drawn inside the overlay like the card.
   */
  @Input() sheetHosted = false;

  /** True while there is a measurement result to show. A calibration shows its own panel instead. */
  get showResult(): boolean {
    return (
      (this.phase !== 'idle' || (this.mode === 'area' && this.areaObjects.length > 0)) && this.mode !== 'calibrate'
    );
  }

  /**
   * The result template for the Preview to draw outside the scrolling page, or null when there is
   * nothing to show or no host has taken it (Issue #2542).
   *
   * This overlay is sized and scrolled with the page. A result drawn inside it sat beside the
   * pointer and got in the way of the drawing (the requester's feedback, and issue #2482). With a
   * host, the result is a panel docked in a corner of the Preview, or a sheet along its bottom on a
   * phone, and neither moves with the page.
   */
  get sheetTemplate(): TemplateRef<unknown> | null {
    return this.showResult && this.sheetHosted ? (this.resultTemplate ?? null) : null;
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onDocumentKeydownListener, { capture: true });
    }
    if (this.recalculatedTimer) clearTimeout(this.recalculatedTimer);
  }

  /** True at phone widths, where a docked result is a sheet along the bottom. */
  private get narrow(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= AREA_SHEET_MAX_WIDTH;
  }

  /**
   * Where the result is shown (Issue #2542).
   *
   * - `panel`: docked in a bottom corner of the Preview, on a wide screen, when the Preview hosts it.
   * - `sheet`: along the bottom of the Preview, at phone widths.
   * - `card`: on the page beside the pointer. This is only the fallback for an overlay that no
   *   host draws for; in the Preview the result is always a panel or a sheet.
   */
  get resultLayout(): 'card' | 'panel' | 'sheet' {
    if (this.sheetHosted) return this.narrow ? 'sheet' : 'panel';
    return this.mode === 'area' && this.narrow ? 'sheet' : 'card';
  }

  /** True when the result is a sheet along the bottom of the Preview. */
  get areaSheet(): boolean {
    return this.resultLayout === 'sheet';
  }

  /** True when the result is the panel docked in a corner of the Preview. */
  get panelLayout(): boolean {
    return this.resultLayout === 'panel';
  }

  /** True when the result is a panel or a sheet, both of which fold to a compact form. */
  get docked(): boolean {
    return this.resultLayout !== 'card';
  }

  /**
   * Which bottom corner the docked panel is in. It lives on the left. While a shape is being drawn
   * and the pointer comes close to it, it moves to the other corner so it is never under the
   * pointer, and it goes back to the left when the shape is finished or cancelled.
   */
  get panelSide(): 'left' | 'right' {
    return this.phase === 'anchored' ? this.dodgedTo : 'left';
  }

  private dodgedTo: 'left' | 'right' = 'left';

  /** How close the pointer may come to the docked panel before the panel moves away, in pixels. */
  private static readonly PANEL_DODGE_MARGIN = 40;

  /** Moves the docked panel to the other corner when the pointer nears it during a gesture. */
  private dodgePointer(clientX: number, clientY: number): void {
    if (!this.panelLayout) return;
    const panel = this.resultCard?.nativeElement.getBoundingClientRect();
    if (!panel || panel.width === 0) return;
    if (pointerNearBox(clientX, clientY, panel, MeasureOverlayComponent.PANEL_DODGE_MARGIN)) {
      this.dodgedTo = this.dodgedTo === 'left' ? 'right' : 'left';
    }
  }

  /**
   * The one-line value shown at the pointer while a shape is being drawn, or null (Issue #2542).
   * It is the only thing beside the pointer: the full result is in the docked panel or the sheet.
   */
  get chipText(): string | null {
    if (this.phase !== 'anchored' || this.scaleDetecting) return null;
    if (this.mode === 'ruler') {
      const total = this.rulerData;
      if (!total) return null;
      return (this.useMetric ? total.physicalMetric : total.physical) ?? total.pdfInches;
    }
    if (this.mode === 'area') {
      if (this.areaShape === 'polygon') return this.polygonVerticesNorm.length + ' corners';
      const area = this.surfaceData;
      if (!area) return null;
      return (this.useMetric ? area.physicalMetric : area.physical) ?? area.pdf;
    }
    return null;
  }

  /**
   * Whether the reviewer has opened the docked result's details (Issue #2542). The panel and the
   * sheet are compact by default: the total and the primary actions, in about a hundred pixels.
   * Expanded, they also show the selected area, the edit controls with Clear all, the overlap
   * choice and the breakdown.
   *
   * An expanded phone sheet can cover the lower part of the drawing, so it folds again whenever a
   * shape is started or finished, and a tap meant for the drawing does not land on its controls.
   * The desktop panel sits in a corner and keeps the reviewer's choice; it only shows its compact
   * form while a shape is being drawn.
   */
  sheetExpanded = false;

  /** True while the docked result shows only the value and the primary actions. */
  get sheetCompact(): boolean {
    return this.docked && (!this.sheetExpanded || this.phase === 'anchored');
  }

  toggleSheet(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.sheetExpanded = !this.sheetExpanded;
  }

  /**
   * Distance of the sheet from the bottom of the Preview's container, in pixels. The container can
   * run past the bottom of the window, so the sheet is lifted by the part that is off screen and
   * always ends just above the window's bottom edge.
   */
  get areaSheetBottom(): number {
    const container = this.overlayEl?.nativeElement.closest('.preview-container');
    if (!container || typeof window === 'undefined') return AREA_SHEET_MARGIN;
    return sheetBottomOffset(container.getBoundingClientRect().bottom, window.innerHeight);
  }

  /**
   * Tallest the expanded sheet may be, in pixels, or null to leave it to the stylesheet. When the
   * page ends above the sheet, as a small sheet does on a phone, the sheet is kept below the page
   * so that the next shape can be drawn anywhere on it. A page that fills the window is necessarily
   * overlapped; there the sheet shrinks to its compact form while a shape is being drawn.
   */
  get areaSheetMaxHeight(): number | null {
    const overlay = this.overlayEl?.nativeElement;
    if (!overlay || typeof window === 'undefined') return null;
    return sheetMaxHeightBelowPage(overlay.getBoundingClientRect().bottom, window.innerHeight);
  }

  /**
   * The Preview toolbar's box in this overlay's coordinates, or null when it is not on the page.
   * While faded out the toolbar is drawn 10 px higher, so that shift is added back: the space is
   * reserved whether or not the toolbar is showing.
   */
  private toolbarInOverlay(): CardBox | null {
    const overlay = this.overlayEl?.nativeElement;
    const toolbar = overlay?.closest('.preview-container')?.querySelector('[data-testid="preview-toolbar"]');
    const content = toolbar?.querySelector('.toolbar-content');
    if (!overlay || !toolbar || !content) return null;
    const box = content.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return null;
    const origin = overlay.getBoundingClientRect();
    const shift = toolbar.classList.contains('visible') ? 0 : 10;
    return { left: box.left - origin.left, top: box.top - origin.top + shift, width: box.width, height: box.height };
  }

  /** Physical measurement text for display. */
  get physicalText(): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!this.rulerData) return null;
    return this.useMetric ? this.rulerData.physicalMetric : this.rulerData.physical;
  }

  get livePhysicalText(): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!this.rulerLiveData) return null;
    return this.useMetric ? this.rulerLiveData.physicalMetric : this.rulerLiveData.physical;
  }

  get areaPhysicalText(): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!this.areaData) return null;
    return this.useMetric ? this.areaData.sizePhysicalMetric : this.areaData.sizePhysical;
  }

  /** Square footage / square meters text for display. */
  get areaSquareText(): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!this.surfaceData) return null;
    return this.useMetric ? this.surfaceData.physicalMetric : this.surfaceData.physical;
  }

  // ── Private helpers ───────────────────────────────────────────

  /** Get pointer position relative to the overlay element. */
  private getOverlayPosition(event: { clientX: number; clientY: number; offsetX: number; offsetY: number }): {
    x: number;
    y: number;
  } {
    const el = this.overlayEl?.nativeElement;
    if (!el) return { x: event.offsetX, y: event.offsetY };

    const rect = el.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  /**
   * Apply Shift-key snapping to the cursor position.
   * - Ruler: snap to the nearest 15° increment from the latest committed vertex.
   * - Rectangle/Oval: constrain the bounding box to equal width and height.
   */
  private applySnapping(rawNormX: number, rawNormY: number, shiftHeld: boolean): { x: number; y: number } {
    const equalSidesConstrained =
      this.mode === 'area' && shiftHeld && (this.areaShape === 'rectangle' || this.areaShape === 'ellipse');
    if (!shiftHeld) {
      return { x: rawNormX, y: rawNormY };
    }
    if (this.mode === 'area' && !equalSidesConstrained) {
      return { x: rawNormX, y: rawNormY };
    }

    if (this.isLineMode) {
      const origin =
        this.mode === 'ruler'
          ? (this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1] ?? {
              x: this.anchorNormX,
              y: this.anchorNormY,
            })
          : { x: this.anchorNormX, y: this.anchorNormY };
      const dx = (rawNormX - origin.x) * this.canvasWidth;
      const dy = (rawNormY - origin.y) * this.canvasHeight;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 1) {
        return { x: rawNormX, y: rawNormY };
      }

      const angleRad = Math.atan2(dy, dx);
      const angleDeg = (angleRad * 180) / Math.PI;
      const snapDeg = MeasureOverlayComponent.SNAP_ANGLE_DEG;
      const snappedDeg = Math.round(angleDeg / snapDeg) * snapDeg;
      const snappedRad = (snappedDeg * Math.PI) / 180;

      const snappedDx = distance * Math.cos(snappedRad);
      const snappedDy = distance * Math.sin(snappedRad);

      return {
        x: origin.x + snappedDx / this.canvasWidth,
        y: origin.y + snappedDy / this.canvasHeight,
      };
    }

    if (equalSidesConstrained) {
      // Equal displayed dimensions produce a square for Rectangle and a circle for Oval.
      const dx = rawNormX - this.anchorNormX;
      const dy = rawNormY - this.anchorNormY;
      const absDx = Math.abs(dx) * this.canvasWidth;
      const absDy = Math.abs(dy) * this.canvasHeight;
      const maxDim = Math.max(absDx, absDy);

      return {
        x: this.anchorNormX + ((dx < 0 ? -1 : 1) * maxDim) / this.canvasWidth,
        y: this.anchorNormY + ((dy < 0 ? -1 : 1) * maxDim) / this.canvasHeight,
      };
    }

    return { x: rawNormX, y: rawNormY };
  }

  /**
   * Recalculate formatted measurement data.
   *
   * When the page's size in points is known, lengths come from the measurement's position on the
   * page (stored as fractions) times that size. Zoom, device pixel ratio and the size of the
   * rendered canvas do not enter the calculation, so they cannot change a reading (Issue #2442).
   * Without the page size the original screen-pixel conversion is used.
   */
  private recalculate(): void {
    const cf = this.pageSizeCorrectionFactor;
    const size = this.pageSizePoints;
    if (this.mode === 'ruler') {
      const totalPoints = this.rulerTotalLengthPoints;
      this.rulerData =
        size && totalPoints !== null
          ? formatDistancePoints(
              totalPoints,
              Math.hypot(size.width, size.height) > 0 ? (totalPoints / Math.hypot(size.width, size.height)) * 1000 : 0,
              DEFAULT_IMAGE_SEARCH_DPI,
              this.drawingScaleFactor,
              cf
            )
          : formatDistance(
              this.rulerTotalLength,
              this.currentScale,
              DEFAULT_IMAGE_SEARCH_DPI,
              this.drawingScaleFactor,
              this.canvasWidth,
              this.canvasHeight,
              cf
            );
      this.rulerLiveData =
        this.phase === 'anchored'
          ? size
            ? formatDistancePoints(
                this.rulerLiveLengthPoints,
                Math.hypot(size.width, size.height) > 0
                  ? (this.rulerLiveLengthPoints / Math.hypot(size.width, size.height)) * 1000
                  : 0,
                DEFAULT_IMAGE_SEARCH_DPI,
                this.drawingScaleFactor,
                cf
              )
            : formatDistance(
                this.rulerLiveLength,
                this.currentScale,
                DEFAULT_IMAGE_SEARCH_DPI,
                this.drawingScaleFactor,
                this.canvasWidth,
                this.canvasHeight,
                cf
              )
          : null;
      this.areaData = null;
      this.surfaceData = null;
    } else if (this.mode === 'calibrate') {
      const points = this.lineLengthPoints;
      if (size && points !== null) {
        const diagonal = Math.hypot(size.width, size.height);
        this.rulerData = formatDistancePoints(
          points,
          diagonal > 0 ? (points / diagonal) * 1000 : 0,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          cf
        );
      } else {
        this.rulerData = formatDistance(
          this.calibrationLineLength,
          this.currentScale,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          this.canvasWidth,
          this.canvasHeight,
          cf
        );
      }
      this.rulerLiveData = null;
      this.areaData = null;
      this.surfaceData = null;
    } else if (this.mode === 'area') {
      const geometry = this.areaGeometry;
      const hasBoundingBox = this.areaShape === 'rectangle' || this.areaShape === 'ellipse';
      if (hasBoundingBox && size) {
        const fractions = {
          left: Math.min(this.anchorNormX, this.cursorNormX),
          top: Math.min(this.anchorNormY, this.cursorNormY),
          width: Math.abs(this.cursorNormX - this.anchorNormX),
          height: Math.abs(this.cursorNormY - this.anchorNormY),
        };
        this.areaData = formatRectanglePoints(
          {
            left: fractions.left * size.width,
            top: fractions.top * size.height,
            width: fractions.width * size.width,
            height: fractions.height * size.height,
          },
          fractions,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          cf
        );
      } else if (hasBoundingBox) {
        this.areaData = formatRectangle(
          this.anchorX,
          this.anchorY,
          this.cursorX,
          this.cursorY,
          this.currentScale,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          this.canvasWidth,
          this.canvasHeight,
          cf
        );
      } else {
        this.areaData = null;
      }

      this.surfaceData = geometry
        ? size
          ? formatSurfaceAreaPoints(
              areaInPagePoints(geometry, size),
              DEFAULT_IMAGE_SEARCH_DPI,
              this.drawingScaleFactor,
              cf
            )
          : formatSurfaceArea(
              this.screenArea(geometry),
              this.currentScale,
              DEFAULT_IMAGE_SEARCH_DPI,
              this.drawingScaleFactor,
              cf
            )
        : null;

      this.rulerData = null;
      this.rulerLiveData = null;
      this.recalculateAreaSession();
    }
  }

  /** Add the completed draft to the cumulative session exactly once. */
  private commitCurrentArea(): void {
    const geometry = this.areaGeometry;
    const size = this.areaCoordinateSize;
    if (!geometry || !size || areaInPagePoints(geometry, size) <= 0) return;
    this.recordAreaMutation();
    const object: AreaObject = { id: this.nextAreaObjectId++, geometry: this.cloneAreaGeometry(geometry) };
    this.areaObjects = [...this.areaObjects, object];
    this.selectedAreaObjectId = object.id;
    if (this.areaSheet) this.sheetExpanded = false;
    this.recalculateAreaSession();
  }

  /** Recalculate committed and live-preview arithmetic/union totals. */
  private recalculateAreaSession(): void {
    const size = this.areaCoordinateSize;
    if (!size) {
      this.areaSessionSummary = null;
      this.areaPreviewSummary = null;
      this.summarizedAreaObjects = null;
      this.summarizedAreaSize = null;
      this.clearAreaSummaryData();
      return;
    }

    const committed = this.areaObjects.map(object => object.geometry);
    const committedSummaryIsCurrent =
      this.areaSessionSummary !== null &&
      this.summarizedAreaObjects === this.areaObjects &&
      this.summarizedAreaSize !== null &&
      this.summarizedAreaSize.width === size.width &&
      this.summarizedAreaSize.height === size.height;
    if (!committedSummaryIsCurrent) {
      this.areaSessionSummary = summarizeAreaUnion(committed, size);
      this.summarizedAreaObjects = this.areaObjects;
      this.summarizedAreaSize = { ...size };
    }
    const preview = this.areaPreviewGeometry;
    this.areaPreviewSummary =
      this.phase === 'anchored' && preview ? summarizeAreaUnion([...committed, preview], size) : null;

    const selected = this.selectedAreaObject;
    this.selectedSurfaceData = selected
      ? this.formatCoordinateSurfaceArea(areaInPagePoints(selected.geometry, size))
      : null;
    this.selectedPerimeterData = selected
      ? this.formatCoordinateLength(perimeterInPagePoints(selected.geometry, size))
      : null;
    this.refreshCumulativeSurfaceData();
  }

  private refreshCumulativeSurfaceData(): void {
    const summary = this.areaSessionSummary;
    if (!summary || this.areaObjects.length === 0) {
      this.cumulativeSurfaceData = null;
      this.grossSurfaceData = null;
      this.overlapSurfaceData = null;
      this.coveredSurfaceData = null;
    } else {
      this.grossSurfaceData = this.formatCoordinateSurfaceArea(summary.grossAreaPoints);
      this.overlapSurfaceData = this.formatCoordinateSurfaceArea(summary.overlapAreaPoints);
      this.coveredSurfaceData = this.formatCoordinateSurfaceArea(summary.coveredAreaPoints);
      this.cumulativeSurfaceData = this.excludeAreaOverlaps ? this.coveredSurfaceData : this.grossSurfaceData;
    }

    const preview = this.areaPreviewSummary;
    this.previewCumulativeSurfaceData = preview
      ? this.formatCoordinateSurfaceArea(this.excludeAreaOverlaps ? preview.coveredAreaPoints : preview.grossAreaPoints)
      : null;

    // The perimeter follows the same choice as the area: the outline of the joined shape when
    // overlaps count once, the sum of every area's own outline when they count twice.
    const perimeterOf = (of: AreaUnionSummary): number =>
      this.excludeAreaOverlaps ? of.coveredPerimeterPoints : of.grossPerimeterPoints;
    this.cumulativePerimeterData =
      summary && this.areaObjects.length > 0 ? this.formatCoordinateLength(perimeterOf(summary)) : null;
    this.previewPerimeterData = preview ? this.formatCoordinateLength(perimeterOf(preview)) : null;
  }

  /** Formats a length given in the coordinates areas are measured in: page points, or screen pixels. */
  private formatCoordinateLength(length: number): FormattedDistance {
    return this.pageSizePoints
      ? formatDistancePoints(
          length,
          0,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          this.pageSizeCorrectionFactor
        )
      : formatDistance(
          length,
          this.currentScale,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          this.canvasWidth,
          this.canvasHeight,
          this.pageSizeCorrectionFactor
        );
  }

  private lengthText(data: FormattedDistance | null): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!data) return null;
    return this.useMetric ? data.physicalMetric : data.physical;
  }

  private clearAreaSummaryData(): void {
    this.cumulativeSurfaceData = null;
    this.previewCumulativeSurfaceData = null;
    this.grossSurfaceData = null;
    this.overlapSurfaceData = null;
    this.coveredSurfaceData = null;
    this.selectedSurfaceData = null;
    this.cumulativePerimeterData = null;
    this.previewPerimeterData = null;
    this.selectedPerimeterData = null;
  }

  /** A valid in-progress shape for a provisional total. */
  private get areaPreviewGeometry(): AreaGeometry | null {
    if (this.mode !== 'area' || this.phase !== 'anchored') return null;
    if (this.areaShape !== 'polygon' && this.areaShape !== 'freehand') return this.areaGeometry;
    const vertices =
      this.areaShape === 'freehand'
        ? simplifyPolygon(this.polygonVerticesNorm)
        : this.polygonVerticesNorm.map(point => ({ ...point }));
    const cursor = { x: this.cursorNormX, y: this.cursorNormY };
    if (this.hasNonZeroPolygonLiveSegment()) vertices.push(cursor);
    return validatePolygon(vertices).valid ? { kind: 'polygon', vertices } : null;
  }

  private get areaCoordinateSize(): PageSizePoints | null {
    if (this.pageSizePoints) return this.pageSizePoints;
    if (this.canvasWidth <= 0 || this.canvasHeight <= 0) return null;
    return { width: this.canvasWidth, height: this.canvasHeight };
  }

  private formatCoordinateSurfaceArea(area: number): FormattedSurfaceArea {
    return this.pageSizePoints
      ? formatSurfaceAreaPoints(area, DEFAULT_IMAGE_SEARCH_DPI, this.drawingScaleFactor, this.pageSizeCorrectionFactor)
      : formatSurfaceArea(
          area,
          this.currentScale,
          DEFAULT_IMAGE_SEARCH_DPI,
          this.drawingScaleFactor,
          this.pageSizeCorrectionFactor
        );
  }

  private surfaceAreaText(data: FormattedSurfaceArea | null): string | null {
    if (this.scaleDetecting) return 'Detecting scale…';
    if (!data) return null;
    return this.useMetric ? data.physicalMetric : data.physical;
  }

  private unionPolygonPath(polygon: AreaUnionPolygon): string {
    return polygon.rings
      .map(ring => {
        if (ring.length === 0) return '';
        const points = ring.map(point => this.toScreenPoint(point));
        return `M ${points.map(point => `${point.x} ${point.y}`).join(' L ')} Z`;
      })
      .filter(Boolean)
      .join(' ');
  }

  private recordAreaMutation(): void {
    this.areaUndoStack.push(this.areaSnapshot());
    if (this.areaUndoStack.length > 100) this.areaUndoStack.shift();
    this.areaRedoStack = [];
  }

  private areaSnapshot(): AreaSessionSnapshot {
    return {
      objects: this.areaObjects.map(object => ({
        id: object.id,
        geometry: this.cloneAreaGeometry(object.geometry),
      })),
      selectedId: this.selectedAreaObjectId,
    };
  }

  private restoreAreaSnapshot(snapshot: AreaSessionSnapshot): void {
    this.areaObjects = snapshot.objects.map(object => ({
      id: object.id,
      geometry: this.cloneAreaGeometry(object.geometry),
    }));
    this.selectedAreaObjectId = snapshot.selectedId;
    this.nextAreaObjectId = Math.max(this.nextAreaObjectId, ...this.areaObjects.map(object => object.id + 1));
    this.resetAreaDraft();
  }

  private cloneAreaGeometry(geometry: AreaGeometry): AreaGeometry {
    if (geometry.kind === 'polygon') {
      return { kind: 'polygon', vertices: geometry.vertices.map(point => ({ ...point })) };
    }
    return { kind: geometry.kind, start: { ...geometry.start }, end: { ...geometry.end } };
  }

  /** Translate a shape by a normalized delta, clamped so every point remains on the page. */
  private translateAreaGeometryWithinPage(geometry: AreaGeometry, dx: number, dy: number): AreaGeometry {
    const bounds = this.areaGeometryBounds(geometry);
    const constrainedX = Math.max(-bounds.minX, Math.min(dx, 1 - bounds.maxX));
    const constrainedY = Math.max(-bounds.minY, Math.min(dy, 1 - bounds.maxY));
    const translate = (point: NormalizedPoint): NormalizedPoint => ({
      x: point.x + constrainedX,
      y: point.y + constrainedY,
    });
    if (geometry.kind === 'polygon') {
      return { kind: 'polygon', vertices: geometry.vertices.map(translate) };
    }
    return { kind: geometry.kind, start: translate(geometry.start), end: translate(geometry.end) };
  }

  private areaGeometryBounds(geometry: AreaGeometry): { minX: number; minY: number; maxX: number; maxY: number } {
    const points = geometry.kind === 'polygon' ? geometry.vertices : [geometry.start, geometry.end];
    return {
      minX: Math.min(...points.map(point => point.x)),
      minY: Math.min(...points.map(point => point.y)),
      maxX: Math.max(...points.map(point => point.x)),
      maxY: Math.max(...points.map(point => point.y)),
    };
  }

  private sameAreaGeometry(first: AreaGeometry, second: AreaGeometry): boolean {
    return JSON.stringify(first) === JSON.stringify(second);
  }

  private screenArea(geometry: AreaGeometry): number {
    if (geometry.kind === 'ellipse') {
      const start = this.toScreenPoint(geometry.start);
      const end = this.toScreenPoint(geometry.end);
      return (Math.PI * Math.abs(end.x - start.x) * Math.abs(end.y - start.y)) / 4;
    }
    if (geometry.kind === 'polygon') {
      return polygonAreaInPagePoints(geometry.vertices, { width: this.canvasWidth, height: this.canvasHeight });
    }
    const start = this.toScreenPoint(geometry.start);
    const end = this.toScreenPoint(geometry.end);
    return Math.abs(end.x - start.x) * Math.abs(end.y - start.y);
  }

  private handlePolygonPointerDown(normX: number, normY: number): void {
    const point = { x: normX, y: normY };
    if (this.phase === 'idle' || this.phase === 'complete') {
      this.resetAreaDraft();
      this.polygonVerticesNorm = [point];
      this.anchorNormX = normX;
      this.anchorNormY = normY;
      this.cursorNormX = normX;
      this.cursorNormY = normY;
      this.phase = 'anchored';
      this.recalculate();
      return;
    }

    this.cursorNormX = normX;
    this.cursorNormY = normY;
    this.commitPolygonPoint(point);
    this.polygonError = null;
    this.recalculate();
  }

  private commitPolygonPoint(point: NormalizedPoint): boolean {
    const previous = this.polygonVerticesNorm[this.polygonVerticesNorm.length - 1];
    if (!previous || this.segmentLength(previous, point) < MeasureOverlayComponent.MIN_SEGMENT_PX) return false;
    const first = this.polygonVerticesNorm[0];
    if (
      this.polygonVerticesNorm.length >= 3 &&
      first &&
      this.segmentLength(first, point) < MeasureOverlayComponent.MIN_SEGMENT_PX
    ) {
      this.cursorNormX = first.x;
      this.cursorNormY = first.y;
      return false;
    }
    this.polygonVerticesNorm.push({ ...point });
    this.cursorNormX = point.x;
    this.cursorNormY = point.y;
    return true;
  }

  private removeLastPolygonVertex(): void {
    this.polygonError = null;
    if (this.polygonVerticesNorm.length <= 1) {
      this.resetAreaDraft();
      return;
    }
    this.polygonVerticesNorm.pop();
    const previous = this.polygonVerticesNorm[this.polygonVerticesNorm.length - 1];
    this.cursorNormX = previous.x;
    this.cursorNormY = previous.y;
    this.recalculate();
  }

  private handleRulerPointerDown(normX: number, normY: number, shiftHeld: boolean): void {
    if (this.phase === 'idle' || this.phase === 'complete') {
      this.rulerVerticesNorm = [{ x: normX, y: normY }];
      this.anchorNormX = normX;
      this.anchorNormY = normY;
      this.cursorNormX = normX;
      this.cursorNormY = normY;
      this.phase = 'anchored';
      this.recalculate();
      return;
    }

    const snapped = this.applySnapping(normX, normY, shiftHeld);
    this.cursorNormX = snapped.x;
    this.cursorNormY = snapped.y;
    this.commitRulerPoint(snapped);
    this.recalculate();
  }

  private isTextEditingTarget(target: EventTarget | null): boolean {
    if (target instanceof HTMLTextAreaElement) return true;
    // A radio button or checkbox is an input, but nothing is typed into it: Escape pressed there,
    // for example in the overlap choice of the area result, still belongs to the tool.
    if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
    if (!(target instanceof HTMLElement)) return false;
    return target.isContentEditable || target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
  }

  private commitRulerPoint(point: NormalizedPoint): boolean {
    const previous = this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1];
    if (!previous || this.segmentLength(previous, point) < MeasureOverlayComponent.MIN_SEGMENT_PX) return false;
    this.rulerVerticesNorm.push({ ...point });
    this.cursorNormX = point.x;
    this.cursorNormY = point.y;
    return true;
  }

  private removeLastRulerVertex(): void {
    if (this.rulerVerticesNorm.length <= 1) {
      this.reset();
      return;
    }
    this.rulerVerticesNorm.pop();
    const previous = this.rulerVerticesNorm[this.rulerVerticesNorm.length - 1];
    this.cursorNormX = previous.x;
    this.cursorNormY = previous.y;
    this.recalculate();
  }

  private hasNonZeroLiveSegment(): boolean {
    return this.rulerLiveLength >= MeasureOverlayComponent.MIN_SEGMENT_PX;
  }

  private hasNonZeroPolygonLiveSegment(): boolean {
    const previous = this.polygonVerticesNorm[this.polygonVerticesNorm.length - 1];
    return (
      !!previous &&
      this.segmentLength(previous, { x: this.cursorNormX, y: this.cursorNormY }) >=
        MeasureOverlayComponent.MIN_SEGMENT_PX
    );
  }

  private segmentLength(start: NormalizedPoint, end: NormalizedPoint): number {
    const dx = (end.x - start.x) * this.canvasWidth;
    const dy = (end.y - start.y) * this.canvasHeight;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private segmentLengthPoints(start: NormalizedPoint, end: NormalizedPoint): number {
    const size = this.pageSizePoints;
    if (!size) return 0;
    return Math.hypot((end.x - start.x) * size.width, (end.y - start.y) * size.height);
  }

  private toScreenPoint(point: NormalizedPoint): { x: number; y: number } {
    return { x: point.x * this.canvasWidth, y: point.y * this.canvasHeight };
  }

  /** Cancel only the shape currently being drawn; committed Area objects remain. */
  private resetAreaDraft(): void {
    this.clearDraftState();
    if (this.mode === 'area') this.recalculateAreaSession();
  }

  /** Reset all measurement and session state when the active tool changes. */
  private reset(): void {
    const hadLine = this.phase === 'complete';
    this.clearDraftState();
    this.areaObjects = [];
    this.selectedAreaObjectId = null;
    this.areaUndoStack = [];
    this.areaRedoStack = [];
    this.areaSessionSummary = null;
    this.areaPreviewSummary = null;
    this.summarizedAreaObjects = null;
    this.summarizedAreaSize = null;
    this.clearAreaSummaryData();
    if (hadLine) this.emitCalibrationLine();
  }

  private clearDraftState(): void {
    this.draggingEnd = null;
    this.freehandPointerId = null;
    this.phase = 'idle';
    this.anchorNormX = 0;
    this.anchorNormY = 0;
    this.cursorNormX = 0;
    this.cursorNormY = 0;
    this.rulerVerticesNorm = [];
    this.polygonVerticesNorm = [];
    this.rulerData = null;
    this.rulerLiveData = null;
    this.areaData = null;
    this.surfaceData = null;
    this.polygonError = null;
    this.areaObjectDrag = null;
  }

  /** Discards the calibration line so a new one can be traced (Issue #2442). */
  redrawCalibrationLine(): void {
    this.reset();
  }
}
